import { Mail, LogOut, User, ChevronDown, Sun, Moon, BarChart3, Settings, Trash2, Menu, X, Gift, CheckCircle, Users, Zap, Calendar, Clock, Infinity, Send, Loader2, AlertCircle, Briefcase } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import type { User as SupabaseUser } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import { getUserPlanLimits } from "../lib/planConfig";

interface NavbarProps {
  user: SupabaseUser | null;
  userPlan?: string;
  view?: "landing" | "dashboard" | "campaign" | "analytics";
  campaignTheme?: "light" | "dark";
  onToggleCampaignTheme?: () => void;
  onGoLanding: () => void;
  onGoDashboard: () => void;
  onGoAnalytics: () => void;
  onGoJobs?: () => void;
  onGoAutoApply?: () => void;
  onLogin: () => void;
  onLogout: () => void;
  onShowUpgrade?: () => void;
}

export default function Navbar({ user, userPlan, view, campaignTheme, onToggleCampaignTheme, onGoLanding, onGoDashboard, onGoAnalytics, onGoJobs, onGoAutoApply, onLogin, onLogout, onShowUpgrade }: NavbarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Refs for click-outside
  const creditRef = useRef<HTMLDivElement>(null);
  const referralRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  
  // Referral state
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [referralCount, setReferralCount] = useState(0);
  const [referralCreditsEarned, setReferralCreditsEarned] = useState(0);
  const [showReferralPanel, setShowReferralPanel] = useState(false);
  const [copiedRef, setCopiedRef] = useState(false);

  // Credit counter state
  const [emailsSent, setEmailsSent] = useState(0);
  const [emailsLimit, setEmailsLimit] = useState(70);
  const [showCreditPanel, setShowCreditPanel] = useState(false);
  const [dailySentCount, setDailySentCount] = useState(0);
  const [monthlySentCount, setMonthlySentCount] = useState(0);

  // Social media submissions
  const [socialSubmissions, setSocialSubmissions] = useState<any[]>([]);
  const [socialPostUrl, setSocialPostUrl] = useState('');
  const [isSubmittingSocial, setIsSubmittingSocial] = useState(false);
  const [socialMessage, setSocialMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  const [showSocialContent, setShowSocialContent] = useState(false);

  const planLimits = getUserPlanLimits(userPlan || 'free');
  const bonusCredits = Math.max(0, emailsLimit - 70);
  const effectiveMonthlyLimit = planLimits.emailsPerMonth !== null ? planLimits.emailsPerMonth + bonusCredits : null;

  useEffect(() => {
    // Always fetch credit limits for logged in user (free or paid)
    if (user) {
      supabase
        .from('profiles')
        .select('emails_sent, emails_limit, referral_code')
        .eq('id', user.id)
        .single()
        .then(({ data }) => {
          if (data) {
            setEmailsSent(data.emails_sent || 0);
            setEmailsLimit(data.emails_limit || 70);
            
            if (data.referral_code && userPlan === 'free') {
              setReferralCode(data.referral_code);
              
              // Get stats
              supabase
                .from('referrals')
                .select('status, signup_credited, subscription_credited')
                .eq('referrer_id', user.id)
                .then(({ data: refs }) => {
                  if (refs) {
                    setReferralCount(refs.length);
                    let credits = 0;
                    refs.forEach(r => {
                      if (r.signup_credited) credits += 20;
                      if (r.subscription_credited) credits += 40;
                    });
                    setReferralCreditsEarned(credits);
                  }
                });

              // Get social submissions
              supabase
                .from('social_submissions')
                .select('*')
                .eq('user_id', user.id)
                .order('created_at', { ascending: false })
                .then(({ data: subs }) => {
                  if (subs) {
                    setSocialSubmissions(subs);
                  }
                });
            }
          }
        });

      // Fetch daily and monthly sent counts from outreach_log
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

      supabase
        .from('outreach_log')
        .select('sent_at, status')
        .eq('user_id', user.id)
        .in('status', ['sent', 'opened'])
        .then(({ data: logs }) => {
          if (logs) {
            let sentToday = 0;
            let sentThisMonth = 0;
            logs.forEach(log => {
              if (log.sent_at) {
                const sentAt = new Date(log.sent_at);
                if (sentAt >= today) sentToday++;
                if (sentAt >= firstDayOfMonth) sentThisMonth++;
              }
            });
            setDailySentCount(sentToday);
            setMonthlySentCount(sentThisMonth);
          }
        });

      const channel = supabase
        .channel('navbar-profile-changes')
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `id=eq.${user.id}` },
          (payload) => {
            if (payload.new.emails_sent !== undefined) setEmailsSent(payload.new.emails_sent);
            if (payload.new.emails_limit !== undefined) setEmailsLimit(payload.new.emails_limit);
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user, userPlan]);

  useEffect(() => {
    const handleCreditsUpdate = (e: any) => {
      if (e.detail && typeof e.detail.count === 'number') {
        setEmailsSent(prev => prev + e.detail.count);
        setDailySentCount(prev => prev + e.detail.count);
        setMonthlySentCount(prev => prev + e.detail.count);
      }
    };
    window.addEventListener('creditsUpdated', handleCreditsUpdate);
    return () => window.removeEventListener('creditsUpdated', handleCreditsUpdate);
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (creditRef.current && !creditRef.current.contains(event.target as Node)) {
        setShowCreditPanel(false);
      }
      if (referralRef.current && !referralRef.current.contains(event.target as Node)) {
        setShowReferralPanel(false);
      }
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSocialSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!socialPostUrl.trim() || !user) return;
    
    setIsSubmittingSocial(true);
    setSocialMessage(null);
    try {
      const { data, error } = await supabase
        .from('social_submissions')
        .insert([{ user_id: user.id, post_url: socialPostUrl }])
        .select()
        .single();
        
      if (error) throw error;
      
      setSocialSubmissions(prev => [data, ...prev]);
      setSocialPostUrl('');
      setSocialMessage({ type: 'success', text: 'After verification by our team the credits will be added to your account.' });
      
      // Clear message after 5s
      setTimeout(() => setSocialMessage(null), 5000);
    } catch (err: any) {
      setSocialMessage({ type: 'error', text: err.message || 'Failed to submit url' });
    } finally {
      setIsSubmittingSocial(false);
    }
  };

  return (
    <nav className="fixed top-4 left-0 right-0 z-50 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl rounded-2xl border border-white/10 bg-zinc-950/70 backdrop-blur-xl shadow-lg shadow-black/20">
        <div className="flex h-16 sm:h-20 items-center justify-between px-5 sm:px-6">
          {/* Logo */}
          <div className="flex items-center cursor-pointer group" onClick={onGoDashboard}>
            <img src="/logo-v2.png" alt="Lynx Reach" className="h-12 sm:h-16 w-auto object-contain group-hover:scale-105 transition-transform origin-left" />
          </div>

          {/* Nav links */}
          <div className="hidden md:flex items-center gap-6">
            {user && (
              <>
                <button
                  onClick={onGoLanding}
                  className={`text-sm font-bold transition-colors ${view === 'landing' ? 'text-indigo-400' : 'text-zinc-400 hover:text-white'}`}
                >
                  Home
                </button>
                <button
                  onClick={onGoDashboard}
                  className={`text-sm font-bold transition-colors ${view === 'dashboard' || view === 'campaign' ? 'text-indigo-400' : 'text-zinc-400 hover:text-white'}`}
                >
                  Dashboard
                </button>
                <button
                  onClick={onGoAnalytics}
                  className={`text-sm font-bold transition-colors flex items-center gap-1.5 ${view === 'analytics' ? 'text-indigo-400' : 'text-zinc-400 hover:text-white'}`}
                >
                  <BarChart3 className="h-4 w-4" />
                  Analytics
                </button>
                <button
                  onClick={onGoJobs}
                  className={`text-sm font-bold transition-colors flex items-center gap-1.5 ${view === 'jobs' ? 'text-indigo-400' : 'text-zinc-400 hover:text-white'}`}
                >
                  <Briefcase className="h-4 w-4" />
                  Job Portal
                </button>
                {/* AUTO APPLY HIDDEN — uncomment to re-enable
                <button
                  onClick={onGoAutoApply}
                  className={`text-sm font-bold transition-colors flex items-center gap-1.5 ${view === 'auto_apply' ? 'text-indigo-400' : 'text-zinc-400 hover:text-white'}`}
                >
                  Auto Apply
                </button>
                */}
              </>
            )}

            {user && (view === "campaign" || view === "dashboard") && (
              <button 
                onClick={onToggleCampaignTheme}
                className="flex items-center justify-center h-8 w-8 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white ml-2"
                title="Toggle Theme"
              >
                {campaignTheme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              </button>
            )}

            {!user && (
              <div className="flex items-center gap-6 ml-4 border-l border-white/10 pl-4">
                {[
                  { label: "Features", id: "features" }, 
                  { label: "Pricing", id: "pricing" }
                ].map((item) => (
                  <button
                    key={item.label}
                    onClick={() => {
                      document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="text-sm font-medium text-zinc-400 hover:text-white transition-colors"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Auth */}
          <div className="flex items-center gap-3">
            {user ? (
              <>
              {/* Credit Counter */}
              <div className="relative" ref={creditRef}>
                <button 
                  onClick={() => {
                    setShowCreditPanel(!showCreditPanel);
                    setShowReferralPanel(false);
                    setMenuOpen(false);
                  }}
                  className="relative flex items-center justify-center h-9 w-9 bg-white/5 hover:bg-white/10 transition-colors rounded-full border border-white/10" 
                  title={`${emailsLimit - emailsSent} credits remaining out of ${emailsLimit}`}
                >
                  <svg className="-rotate-90 transform w-9 h-9">
                    <circle cx="18" cy="18" r="15" fill="transparent" stroke="rgba(255,255,255,0.05)" strokeWidth="3" />
                    <circle 
                      cx="18" 
                      cy="18" 
                      r="15" 
                      fill="transparent" 
                      stroke="currentColor" 
                      strokeWidth="3" 
                      strokeDasharray={2 * Math.PI * 15} 
                      strokeDashoffset={(2 * Math.PI * 15) - ((emailsSent / emailsLimit) * (2 * Math.PI * 15))} 
                      className="text-blue-500 transition-all duration-500 ease-out" 
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white tabular-nums">
                    {emailsLimit - emailsSent}
                  </div>
                </button>

                {showCreditPanel && (
                  <div className="absolute right-0 top-full mt-2 w-80 rounded-2xl border border-white/10 bg-zinc-900/95 backdrop-blur-xl p-5 shadow-xl animate-in fade-in slide-in-from-top-2 duration-200">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded-lg bg-blue-500/10 flex items-center justify-center">
                          <Zap className="h-3.5 w-3.5 text-blue-400" />
                        </div>
                        <h4 className="text-sm font-bold text-white">Your Credits</h4>
                      </div>
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                        userPlan === 'free' ? 'bg-zinc-800 text-zinc-400' :
                        userPlan === 'starter' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' :
                        userPlan === 'pro' ? 'bg-violet-500/10 text-violet-400 border border-violet-500/20' :
                        'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                      }`}>{userPlan || 'free'}</span>
                    </div>

                    {/* Credit Rows */}
                    <div className="space-y-2.5">
                      {/* Today's Credits - shown for ALL plans */}
                      <div className="flex items-center justify-between p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                        <div className="flex items-center gap-2.5">
                          <div className="h-8 w-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                            <Clock className="h-4 w-4 text-emerald-400" />
                          </div>
                          <div>
                            <p className="text-xs font-bold text-white">Today</p>
                            <p className="text-[10px] text-zinc-500">Resets at midnight</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-white tabular-nums">
                            {planLimits.emailsPerDay !== null ? Math.max(0, planLimits.emailsPerDay - dailySentCount) : '∞'}
                            <span className="text-zinc-500 font-normal"> / {planLimits.emailsPerDay ?? '∞'}</span>
                          </p>
                          <p className="text-[10px] text-zinc-500">remaining</p>
                        </div>
                      </div>

                      {effectiveMonthlyLimit !== null && (
                        <div className="flex items-center justify-between p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                          <div className="flex items-center gap-2.5">
                            <div className="h-8 w-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                              <Calendar className="h-4 w-4 text-blue-400" />
                            </div>
                            <div>
                              <p className="text-xs font-bold text-white">This Month</p>
                              <p className="text-[10px] text-zinc-500">Resets on 1st</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-white tabular-nums">
                              {Math.max(0, effectiveMonthlyLimit - monthlySentCount)}
                              <span className="text-zinc-500 font-normal"> / {effectiveMonthlyLimit}</span>
                            </p>
                            <p className="text-[10px] text-zinc-500">remaining</p>
                          </div>
                        </div>
                      )}

                      {/* Unlimited badge for annual */}
                      {userPlan === 'annual' && (
                        <div className="flex items-center justify-between p-3 rounded-xl bg-amber-500/5 border border-amber-500/10">
                          <div className="flex items-center gap-2.5">
                            <div className="h-8 w-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                              <Infinity className="h-4 w-4 text-amber-400" />
                            </div>
                            <div>
                              <p className="text-xs font-bold text-white">Monthly</p>
                              <p className="text-[10px] text-amber-400/70">Unlimited emails</p>
                            </div>
                          </div>
                          <span className="text-xs font-bold text-amber-400">∞</span>
                        </div>
                      )}

                      {/* Lifetime Credits - shown only for free users */}
                      {userPlan === 'free' && (
                        <div className="flex items-center justify-between p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                          <div className="flex items-center gap-2.5">
                            <div className="h-8 w-8 rounded-lg bg-violet-500/10 flex items-center justify-center">
                              <Mail className="h-4 w-4 text-violet-400" />
                            </div>
                            <div>
                              <p className="text-xs font-bold text-white">Total Available</p>
                              <p className="text-[10px] text-zinc-500">Lifetime credits</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-white tabular-nums">
                              {Math.max(0, emailsLimit - emailsSent)}
                              <span className="text-zinc-500 font-normal"> / {emailsLimit}</span>
                            </p>
                            <p className="text-[10px] text-zinc-500">remaining</p>
                          </div>
                        </div>
                      )}

                      {/* Total Sent - shown for paid users */}
                      {userPlan !== 'free' && (
                        <div className="flex items-center justify-between p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                          <div className="flex items-center gap-2.5">
                            <div className="h-8 w-8 rounded-lg bg-violet-500/10 flex items-center justify-center">
                              <Mail className="h-4 w-4 text-violet-400" />
                            </div>
                            <div>
                              <p className="text-xs font-bold text-white">Total Sent</p>
                              <p className="text-[10px] text-zinc-500">All time</p>
                            </div>
                          </div>
                          <p className="text-sm font-bold text-white tabular-nums">{emailsSent}</p>
                        </div>
                      )}

                      {/* Social Credits Earned */}
                      {socialSubmissions.filter(s => s.status === 'approved').length > 0 && (
                        <div className="flex items-center justify-between p-3 rounded-xl bg-blue-500/5 border border-blue-500/10 mt-2">
                          <div className="flex items-center gap-2.5">
                            <div className="h-8 w-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                              <Send className="h-4 w-4 text-blue-400" />
                            </div>
                            <div>
                              <p className="text-xs font-bold text-white">Post Bonus</p>
                              <p className="text-[10px] text-zinc-500">From social media</p>
                            </div>
                          </div>
                          <p className="text-sm font-bold text-blue-400 tabular-nums">
                            +{socialSubmissions.filter(s => s.status === 'approved').length * 60}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Upgrade Button - Free users only */}
                    {userPlan === 'free' && (
                      <button 
                        onClick={() => {
                          setShowCreditPanel(false);
                          window.dispatchEvent(new Event('show-upgrade'));
                        }}
                        className="w-full mt-4 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 py-2.5 text-xs font-bold text-white hover:from-blue-500 hover:to-violet-500 transition-all"
                      >
                        Upgrade for more credits
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Refer & Earn Dropdown */}
              {referralCode && (
                <div className="relative hidden md:block" ref={referralRef}>
                  <button
                    onClick={() => {
                      setShowReferralPanel(!showReferralPanel);
                      setShowCreditPanel(false);
                      setMenuOpen(false);
                    }}
                    className={`flex items-center gap-2 rounded-full border px-3 py-1.5 h-9 transition-all group shrink-0 ${
                      showReferralPanel 
                        ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-400'
                        : 'border-emerald-500/20 bg-emerald-500/5 text-emerald-400 hover:bg-emerald-500/10'
                    }`}
                  >
                    <Gift className="h-4 w-4" />
                    <span className="text-sm font-bold whitespace-nowrap">Refer & Earn</span>
                  </button>
                  
                  {showReferralPanel && (
                    <div className="absolute right-0 top-full mt-2 w-[400px] max-h-[85vh] overflow-y-auto rounded-2xl border border-white/10 bg-zinc-900/95 backdrop-blur-xl p-4 shadow-xl animate-in fade-in slide-in-from-top-2 duration-200 custom-scrollbar">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-emerald-400" />
                          <span className="text-sm font-bold text-white">Your Referral Link</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 mb-3">
                        <div className="flex-1 rounded-lg border border-white/10 bg-black/40 px-3 py-2 font-mono text-[11px] text-zinc-400 truncate">
                          lynxreach.com/?ref={referralCode}
                        </div>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(`https://lynxreach.com/?ref=${referralCode}`);
                            setCopiedRef(true);
                            setTimeout(() => setCopiedRef(false), 2000);
                          }}
                          className="shrink-0 flex items-center justify-center rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-zinc-300 hover:bg-white/10 transition-colors"
                        >
                          {copiedRef ? <CheckCircle className="h-4 w-4 text-emerald-400" /> : <span className="text-xs font-semibold">Copy</span>}
                        </button>
                        <a
                          href={`https://wa.me/?text=${encodeURIComponent(`Hey! I found this AI tool for Indian job seekers that lets you email HRs directly to ask for referrals. It gives 50 free limits!\n\nSign up using my link: https://lynxreach.com/?ref=${referralCode}`)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="shrink-0 flex items-center justify-center rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-3 py-2 text-emerald-400 hover:bg-emerald-500/20 transition-colors"
                        >
                          <span className="text-xs font-semibold">Share</span>
                        </a>
                      </div>
                      
                      <div className="flex items-center gap-1.5 text-[10px] text-zinc-500 mb-2 border-b border-white/5 pb-3">
                        <Gift className="h-3 w-3 text-emerald-500/70 shrink-0" />
                        <p>Friend signs up &rarr; you get <strong className="text-emerald-400/80 font-semibold">+20 credits</strong><br/>Friend subscribes &rarr; you get <strong className="text-emerald-400/80 font-semibold">+40 more credits</strong></p>
                      </div>

                      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-2.5 mb-3 flex items-start gap-2">
                        <AlertCircle className="h-3 w-3 text-amber-500 shrink-0 mt-0.5" />
                        <p className="text-[10px] text-zinc-400 leading-tight">
                          Credits increase your <span className="text-amber-500 font-semibold">lifetime limit</span>. Free plans are still subject to a safe sending speed of <span className="text-amber-500 font-semibold">10 emails/day</span> to protect your account.
                        </p>
                      </div>

                      <div className="flex items-center justify-between text-xs mb-6">
                        <div className="text-zinc-400">
                          Friends Referred: <span className="text-white font-bold">{referralCount}</span>
                        </div>
                        <div className="text-zinc-400">
                          Ref Credits: <span className="text-emerald-400 font-bold">+{referralCreditsEarned}</span>
                        </div>
                      </div>

                      {/* Social Media Submissions */}
                      <div className="border-t border-white/10 pt-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Send className="h-4 w-4 text-blue-400" />
                            <span className="text-sm font-bold text-white">Post on Social Media</span>
                          </div>
                          <button 
                            onClick={() => setShowSocialContent(!showSocialContent)}
                            className="text-[10px] font-semibold bg-white/5 hover:bg-white/10 text-zinc-300 px-2 py-1 rounded-md transition-colors border border-white/10"
                          >
                            Need content?
                          </button>
                        </div>
                        <p className="text-[11px] text-zinc-400 mb-3">
                          Post about LYNX Reach on LinkedIn or Twitter and paste the link below to get <strong className="text-blue-400">bonus credits</strong>!
                        </p>

                        {showSocialContent && (
                          <div className="mb-4 p-3 rounded-xl bg-blue-500/5 border border-blue-500/20 relative group">
                            <p className="text-[11px] text-zinc-300 italic mb-2 leading-relaxed">
                              "Applying to 100 jobs on LinkedIn = 0 replies.<br/>
                              Sending 10 direct emails to HRs = 3 interviews.<br/><br/>
                              I use Lynx Reach to automatically find HR emails and let AI write the perfect pitch based on my resume.<br/><br/>
                              If you are struggling to get interviews, try this method instead 👇<br/>
                              https://lynxreach.com/?ref={referralCode}<br/><br/>
                              #Freshers #JobHunt #LYNXReach"
                            </p>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText("Applying to 100 jobs on LinkedIn = 0 replies.\nSending 10 direct emails to HRs = 3 interviews.\n\nI use Lynx Reach to automatically find HR emails and let AI write the perfect pitch based on my resume.\n\nIf you are struggling to get interviews, try this method instead 👇\n" + `https://lynxreach.com/?ref=${referralCode}` + "\n\n#Freshers #JobHunt #LYNXReach");
                                setSocialMessage({ type: 'success', text: 'Content copied to clipboard!' });
                                setTimeout(() => setSocialMessage(null), 3000);
                              }}
                              className="w-full mt-1 flex items-center justify-center rounded-lg bg-blue-500/10 border border-blue-500/20 py-1.5 text-[10px] font-bold text-blue-400 hover:bg-blue-500/20 transition-colors"
                            >
                              Copy Text
                            </button>
                          </div>
                        )}
                        
                        <form onSubmit={handleSocialSubmit} className="flex flex-col gap-2 mb-4">
                          <input 
                            type="url" 
                            required
                            placeholder="https://linkedin.com/post/..." 
                            value={socialPostUrl}
                            onChange={(e) => setSocialPostUrl(e.target.value)}
                            className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-xs text-white placeholder:text-zinc-600 focus:outline-none focus:border-blue-500/50"
                          />
                          <button 
                            type="submit"
                            disabled={isSubmittingSocial}
                            className="w-full flex items-center justify-center gap-2 rounded-lg bg-blue-500/10 border border-blue-500/20 py-2 text-xs font-bold text-blue-400 hover:bg-blue-500/20 transition-colors disabled:opacity-50"
                          >
                            {isSubmittingSocial ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Submit for Verification'}
                          </button>
                        </form>

                        {socialMessage && (
                          <div className={`p-2.5 rounded-lg text-xs mb-4 ${socialMessage.type === 'success' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'}`}>
                            {socialMessage.text}
                          </div>
                        )}

                        {socialSubmissions.length > 0 && (
                          <div className="space-y-2 max-h-[150px] overflow-y-auto pr-1 custom-scrollbar">
                            <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-2">Your Submissions</p>
                            {socialSubmissions.map((sub, idx) => (
                              <div key={sub.id} className="flex flex-col gap-1.5 p-2.5 rounded-lg bg-white/5 border border-white/10">
                                <div className="flex items-center justify-between">
                                  <span className="text-[11px] text-zinc-300 font-medium truncate max-w-[180px]">
                                    {sub.post_url}
                                  </span>
                                  <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${
                                    sub.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' :
                                    sub.status === 'rejected' ? 'bg-rose-500/20 text-rose-400' :
                                    'bg-amber-500/20 text-amber-400'
                                  }`}>
                                    {sub.status === 'approved' ? 'Added credits' :
                                     sub.status === 'rejected' ? 'Rejected' :
                                     'Sent to team, waiting'}
                                  </span>
                                </div>
                                <span className="text-[9px] text-zinc-600">
                                  {new Date(sub.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => {
                    setMenuOpen(!menuOpen);
                    setShowCreditPanel(false);
                    setShowReferralPanel(false);
                  }}
                  className="flex items-center gap-2.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 transition-colors hover:bg-white/10"
                >
                  {user.user_metadata?.avatar_url ? (
                    <img
                      src={user.user_metadata.avatar_url}
                      alt="avatar"
                      className="h-6 w-6 rounded-full"
                    />
                  ) : (
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-white/20">
                      <User className="h-3.5 w-3.5 text-white" />
                    </div>
                  )}
                  <span className="text-sm text-zinc-300 font-medium max-w-[120px] truncate">
                    {user.user_metadata?.full_name ?? user.email}
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 text-zinc-500" />
                </button>

                {menuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-44 rounded-2xl border border-white/10 bg-zinc-900 p-1 shadow-xl">
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        onLogout();
                      }}
                      className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-zinc-400 hover:bg-white/5 hover:text-white transition-colors"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign out
                    </button>
                  </div>
                )}
              </div>

              {/* Theme Toggle (Visible everywhere except landing) */}
              {view !== 'landing' && (
                <button
                  onClick={() => onToggleCampaignTheme?.()}
                  className="hidden sm:flex items-center justify-center rounded-full border border-white/10 bg-white/5 p-2 transition-colors hover:bg-white/10"
                  title={campaignTheme === 'light' ? "Switch to Dark Mode" : "Switch to Light Mode"}
                >
                  {campaignTheme === 'light' ? (
                    <Moon className="h-4 w-4 text-zinc-400" />
                  ) : (
                    <Sun className="h-4 w-4 text-zinc-400" />
                  )}
                </button>
              )}

              {/* Settings Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setSettingsOpen(!settingsOpen)}
                  className="flex items-center justify-center rounded-full border border-white/10 bg-white/5 p-2 transition-colors hover:bg-white/10"
                >
                  <Settings className="h-4 w-4 text-zinc-400" />
                </button>

                {settingsOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 rounded-2xl border border-rose-500/20 bg-zinc-900 p-1 shadow-xl">
                    <button
                      onClick={async () => {
                        if (window.confirm("Are you absolutely sure you want to delete your account and all data? This cannot be undone.")) {
                          try {
                            const { error } = await supabase.rpc('delete_user_account');
                            if (error) throw error;
                            await supabase.auth.signOut();
                            window.location.reload();
                          } catch (err) {
                            console.error("Failed to delete account", err);
                            alert("Failed to delete account. Please try again.");
                          }
                        }
                      }}
                      className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm text-rose-500 font-medium hover:bg-rose-500/10 transition-colors text-left"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete Account & Data
                    </button>
                  </div>
                )}
              </div>
            </>
            ) : (
              <button
                onClick={onLogin}
                className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2 text-sm font-semibold text-zinc-950 transition-all hover:bg-zinc-200 active:scale-[0.98]"
              >
                Sign in with Google
              </button>
            )}

            {/* Mobile Menu Button - Moved to Right Side */}
            <button 
              className="md:hidden flex items-center justify-center p-1.5 rounded-lg border border-white/10 bg-white/5 text-white hover:bg-white/10 transition-colors ml-1"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/10 px-4 py-4 space-y-4 bg-zinc-950/95 rounded-b-2xl max-h-[85vh] overflow-y-auto custom-scrollbar">
            {user ? (
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => { setMobileMenuOpen(false); onGoLanding?.(); }}
                  className={`text-sm font-bold text-left transition-colors ${view === 'landing' ? 'text-indigo-400' : 'text-zinc-400 hover:text-white'}`}
                >
                  Home
                </button>
                <button
                  onClick={() => { setMobileMenuOpen(false); onGoDashboard?.(); }}
                  className={`text-sm font-bold text-left transition-colors ${view === 'dashboard' ? 'text-indigo-400' : 'text-zinc-400 hover:text-white'}`}
                >
                  Dashboard
                </button>
                <button
                  onClick={() => { setMobileMenuOpen(false); onGoAnalytics?.(); }}
                  className={`text-sm font-bold text-left transition-colors flex items-center gap-2 ${view === 'analytics' ? 'text-indigo-400' : 'text-zinc-400 hover:text-white'}`}
                >
                  <BarChart3 className="h-4 w-4" />
                  Analytics
                </button>
                <button
                  onClick={() => { setMobileMenuOpen(false); onGoJobs?.(); }}
                  className={`text-sm font-bold text-left transition-colors flex items-center gap-2 ${view === 'jobs' ? 'text-indigo-400' : 'text-zinc-400 hover:text-white'}`}
                >
                  <Briefcase className="h-4 w-4" />
                  Job Portal
                </button>
                {/* AUTO APPLY HIDDEN — uncomment to re-enable
                <button
                  onClick={() => { setMobileMenuOpen(false); onGoAutoApply?.(); }}
                  className={`text-sm font-bold text-left transition-colors flex items-center gap-2 ${view === 'auto_apply' ? 'text-indigo-400' : 'text-zinc-400 hover:text-white'}`}
                >
                  <Send className="h-4 w-4" />
                  Auto Apply
                </button>
                */}

                {/* Refer & Earn Mobile */}
                {referralCode && (
                  <div className="mt-2 pt-3 border-t border-white/10">
                    <div className="flex items-center gap-2 mb-3">
                      <Gift className="h-4 w-4 text-emerald-400" />
                      <span className="text-sm font-bold text-emerald-400">Refer & Earn</span>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="rounded-lg border border-white/10 bg-black/40 px-3 py-2 font-mono text-[11px] text-zinc-400 truncate">
                        lynxreach.com/?ref={referralCode}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(`https://lynxreach.com/?ref=${referralCode}`);
                            setCopiedRef(true);
                            setTimeout(() => setCopiedRef(false), 2000);
                          }}
                          className="flex-1 flex items-center justify-center rounded-lg bg-white/5 border border-white/10 py-2 text-zinc-300 hover:bg-white/10 transition-colors"
                        >
                          {copiedRef ? <CheckCircle className="h-4 w-4 text-emerald-400" /> : <span className="text-xs font-semibold">Copy Link</span>}
                        </button>
                        <a
                          href={`https://wa.me/?text=${encodeURIComponent(`Hey! I found this AI tool for Indian job seekers that lets you email HRs directly to ask for referrals. It gives 50 free limits!\n\nSign up using my link: https://lynxreach.com/?ref=${referralCode}`)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex-1 flex items-center justify-center rounded-lg bg-emerald-500/10 border border-emerald-500/20 py-2 text-emerald-400 hover:bg-emerald-500/20 transition-colors"
                        >
                          <span className="text-xs font-semibold">Share</span>
                        </a>
                      </div>
                      <div className="flex items-center justify-between text-xs mt-1">
                        <div className="text-zinc-400">Referred: <span className="text-white font-bold">{referralCount}</span></div>
                        <div className="text-zinc-400">Credits: <span className="text-emerald-400 font-bold">+{referralCreditsEarned}</span></div>
                      </div>
                    </div>

                    {/* Social Media Submissions Mobile */}
                    <div className="border-t border-white/10 mt-3 pt-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Send className="h-4 w-4 text-blue-400" />
                          <span className="text-sm font-bold text-white">Post on Social Media</span>
                        </div>
                        <button 
                          onClick={() => setShowSocialContent(!showSocialContent)}
                          className="text-[10px] font-semibold bg-white/5 hover:bg-white/10 text-zinc-300 px-2 py-1 rounded-md transition-colors border border-white/10"
                        >
                          Need content?
                        </button>
                      </div>
                      <p className="text-[11px] text-zinc-400 mb-3">
                        Post about LYNX Reach on LinkedIn or Twitter and paste the link below to get <strong className="text-blue-400">bonus credits</strong>!
                      </p>

                      {showSocialContent && (
                        <div className="mb-4 p-3 rounded-xl bg-blue-500/5 border border-blue-500/20 relative group">
                          <p className="text-[11px] text-zinc-300 italic mb-2 leading-relaxed">
                            "Tired of Naukri black holes?<br/><br/>
                            I switched to reaching HRs directly.<br/>
                            47 introductions &rarr; 3 replies &rarr; 2 interviews.<br/><br/>
                            Tool I used: Lynx Reach<br/>
                            AI writes the email. Sends from your Gmail.<br/>
                            HRs actually respond.<br/><br/>
                            Free to try 👇<br/>
                            https://lynxreach.com/?ref={referralCode}<br/><br/>
                            #JobSearch #LYNXReach #Hiring #Freshers"
                          </p>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText("Tired of Naukri black holes?\n\nI switched to reaching HRs directly.\n47 introductions → 3 replies → 2 interviews.\n\nTool I used: Lynx Reach\nAI writes the email. Sends from your Gmail.\nHRs actually respond.\n\nFree to try 👇\n" + `https://lynxreach.com/?ref=${referralCode}` + "\n\n#JobSearch #LYNXReach #Hiring #Freshers");
                              setSocialMessage({ type: 'success', text: 'Content copied to clipboard!' });
                              setTimeout(() => setSocialMessage(null), 3000);
                            }}
                            className="w-full mt-1 flex items-center justify-center rounded-lg bg-blue-500/10 border border-blue-500/20 py-1.5 text-[10px] font-bold text-blue-400 hover:bg-blue-500/20 transition-colors"
                          >
                            Copy Text
                          </button>
                        </div>
                      )}
                      
                      <form onSubmit={handleSocialSubmit} className="flex flex-col gap-2 mb-4">
                        <input 
                          type="url" 
                          required
                          placeholder="https://linkedin.com/post/..." 
                          value={socialPostUrl}
                          onChange={(e) => setSocialPostUrl(e.target.value)}
                          className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-xs text-white placeholder:text-zinc-600 focus:outline-none focus:border-blue-500/50"
                        />
                        <button 
                          type="submit"
                          disabled={isSubmittingSocial}
                          className="w-full flex items-center justify-center gap-2 rounded-lg bg-blue-500/10 border border-blue-500/20 py-2 text-xs font-bold text-blue-400 hover:bg-blue-500/20 transition-colors disabled:opacity-50"
                        >
                          {isSubmittingSocial ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Submit for Verification'}
                        </button>
                      </form>

                      {socialMessage && (
                        <div className={`p-2.5 rounded-lg text-xs mb-4 ${socialMessage.type === 'success' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'}`}>
                          {socialMessage.text}
                        </div>
                      )}

                      {socialSubmissions.length > 0 && (
                        <div className="space-y-2 max-h-[150px] overflow-y-auto pr-1 custom-scrollbar">
                          <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-2">Your Submissions</p>
                          {socialSubmissions.map((sub, idx) => (
                            <div key={sub.id} className="flex flex-col gap-1.5 p-2.5 rounded-lg bg-white/5 border border-white/10">
                              <div className="flex items-center justify-between">
                                <span className="text-[11px] text-zinc-300 font-medium truncate max-w-[180px]">
                                  {sub.post_url}
                                </span>
                                <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${
                                  sub.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' :
                                  sub.status === 'rejected' ? 'bg-rose-500/20 text-rose-400' :
                                  'bg-amber-500/20 text-amber-400'
                                }`}>
                                  {sub.status === 'approved' ? 'Added credits' :
                                   sub.status === 'rejected' ? 'Rejected' :
                                   'Sent to team, waiting'}
                                </span>
                              </div>
                              <span className="text-[9px] text-zinc-600">
                                {new Date(sub.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {[
                  { label: "Features", id: "features" }, 
                  { label: "Pricing", id: "pricing" }
                ].map((item) => (
                  <button
                    key={item.label}
                    onClick={() => {
                      setMobileMenuOpen(false);
                      document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="text-sm font-medium text-left text-zinc-400 hover:text-white transition-colors"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
